// ============================================
// ChartMenu — the one shell every small chart menu sits in.
//
// Frame:   <ChartMenu anchor={{x,y}} onClose title openLeft>…</ChartMenu>
//          Background / border / radius / shadow / font / padding / z-index
//          all come from the --menu-* variables in styles.css ("CHART MENUS"
//          block). Change a variable there and every menu changes.
// Fit:     useFitToScreen — real measured size, 8px viewport margin, flips
//          left / above when the preferred side has no room. Capped at
//          100vh - 16px (or the caller's maxHeight) and scrolls internally
//          only when it is actually taller, so dropdowns inside aren't
//          clipped in the normal case. Re-fits on content resize
//          (ResizeObserver) and window resize.
// Close:   mousedown outside (window, BUBBLE phase — ColorPicker portals its
//          panel to body and defends itself with stopPropagation, which only
//          a bubble listener respects) and Escape. Listeners attach one tick
//          after open so the gesture that opened the menu can't close it.
// Phone:   `wide` → full-width sheet on html.is-phone (the convention the
//          right-click and beaker menus already used); coarse pointers get
//          bigger touch targets through the variables.
//
// Primitives below: MenuSection, MenuRow, MenuLabel, MenuToggle, MenuSelect,
// MenuWeight, MenuNumber, MenuText, MenuSwatch, MenuButton, MenuActions,
// MenuStepper, MenuDivider, MenuItem, MenuItemRow.
// ============================================
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import useFitToScreen from '../hooks/useFitToScreen'
import ColorPicker from '../ColorPicker'

const cx = (...a) => a.filter(Boolean).join(' ')

export default function ChartMenu({
  show = true,
  anchor,                 // { x, y } — click point (or right edge when openLeft)
  openLeft = false,
  onClose,
  title,                  // optional header text / node
  closeButton = false,    // ✕ in the header
  wide = false,           // phone: full-width sheet
  width,                  // fixed width (px) when a menu needs one
  maxHeight,              // optional extra cap (px), e.g. above a bottom rail
  className,
  style,
  ignoreRefs,             // refs whose clicks don't count as "outside" (the opener button)
  closeOnContextMenu = false,
  closeOnEscape = true,
  closeOnTouchOutside = false, // also close on touchstart outside (chart touch handlers preventDefault → no compat mousedown)
  stopMouseDown = false,  // swallow mousedown so the chart under it never sees it
  panelRef,               // optional external ref to the frame element
  draggable = false,      // drag the frame by its title bar (zindex-20261102)
  children,
}) {
  const ref = useRef(null)
  const setRef = (el) => {
    ref.current = el
    if (panelRef) { if (typeof panelRef === 'function') panelRef(el); else panelRef.current = el }
  }
  // Drag offset from the anchor; reset each time the menu opens.
  const [drag, setDrag] = useState({ dx: 0, dy: 0 })
  useEffect(() => { if (show) setDrag({ dx: 0, dy: 0 }) }, [show])
  const x = (anchor?.x ?? 0) + drag.dx
  const y = (anchor?.y ?? 0) + drag.dy
  const onTitleDown = (e) => {
    if (!draggable || e.button > 0 || e.target.closest?.('.cm-title-x')) return
    e.preventDefault(); e.stopPropagation()
    const el = e.currentTarget, sx = e.clientX, sy = e.clientY, d0 = drag
    try { el.setPointerCapture(e.pointerId) } catch {}
    const move = (ev) => setDrag({ dx: d0.dx + ev.clientX - sx, dy: d0.dy + ev.clientY - sy })
    const up = () => { el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up) }
    el.addEventListener('pointermove', move); el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up)
  }

  // Fit before paint on every render; the tick below forces a render when
  // the content or the window changes size.
  const [, setTick] = useState(0)
  const [tooTall, setTooTall] = useState(false)
  useFitToScreen(ref, show, x, y, openLeft)
  useLayoutEffect(() => {
    const el = ref.current
    if (!show || !el) return
    const cap = Math.min(window.innerHeight - 16, Number.isFinite(maxHeight) ? maxHeight : Infinity)
    const tall = el.scrollHeight > cap + 1
    if (tall !== tooTall) setTooTall(tall)
  })
  useEffect(() => {
    if (!show) return
    const bump = () => setTick(t => t + 1)
    window.addEventListener('resize', bump)
    let ro = null
    const el = ref.current
    if (el && typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(bump); ro.observe(el) }
    return () => { window.removeEventListener('resize', bump); if (ro) ro.disconnect() }
  }, [show])

  // Outside-click / Escape. Latest onClose via ref so callers can pass inline
  // arrows without re-subscribing every render.
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const ignoreRef = useRef(ignoreRefs)
  ignoreRef.current = ignoreRefs
  useEffect(() => {
    if (!show) return
    const inside = (t) => {
      if (ref.current && ref.current.contains(t)) return true
      if (t?.closest?.('[data-cm-keep]')) return true   // ColorPicker panel (portaled to body)
      const ig = ignoreRef.current
      if (ig) for (const r of ig) if (r?.current && r.current.contains(t)) return true
      return false
    }
    // Touch: a phone menu opens from a LONG-PRESS while the finger is still
    // down, so the lift's compat mousedown and the browser's own long-press
    // contextmenu can land just outside the (clamped/flipped) frame. Ignore
    // outside events for a moment after opening on coarse pointers.
    const openedAt = Date.now()
    const coarse = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches
    const fresh = () => coarse && Date.now() - openedAt < 800
    const onDown = (e) => { if (!fresh() && !inside(e.target)) closeRef.current?.() }
    const onCtx = (e) => { if (!fresh() && !inside(e.target)) closeRef.current?.() }
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current?.() }
    const t = setTimeout(() => {
      window.addEventListener('mousedown', onDown)
      if (closeOnContextMenu) window.addEventListener('contextmenu', onCtx)
      if (closeOnEscape) window.addEventListener('keydown', onKey)
      if (closeOnTouchOutside) window.addEventListener('touchstart', onDown, { passive: true })
    }, 0)
    return () => {
      clearTimeout(t)
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('contextmenu', onCtx)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('touchstart', onDown)
    }
  }, [show, closeOnContextMenu, closeOnEscape, closeOnTouchOutside])

  if (!show) return null

  const capStyle = Number.isFinite(maxHeight)
    ? { maxHeight: `min(${Math.max(80, maxHeight)}px, calc(100vh - 16px))` }
    : null

  return (
    <div
      ref={setRef}
      className={cx('cm-menu', wide && 'cm-wide', tooTall && 'cm-scroll', className)}
      style={{ left: x, top: y, ...(width ? { width } : null), ...capStyle, ...style }}
      onMouseDown={stopMouseDown ? (e) => e.stopPropagation() : undefined}
      role="menu"
    >
      {title != null && (
        <div className="cm-title" onPointerDown={draggable ? onTitleDown : undefined}
          style={draggable ? { cursor: 'move', touchAction: 'none', userSelect: 'none' } : undefined}>
          <span>{title}</span>
          {closeButton && <span className="cm-title-x" onClick={() => closeRef.current?.()}>✕</span>}
        </div>
      )}
      {children}
    </div>
  )
}

// ─── Layout ─────────────────────────────────────────────────────────────

// A titled group. collapsible → click the title to fold; storageKey keeps
// the folded state across reloads.
export function MenuSection({ title, collapsible = false, defaultOpen = true, storageKey, className, children }) {
  const [open, setOpen] = useState(() => {
    if (!collapsible) return true
    if (storageKey) { try { const v = localStorage.getItem(storageKey); if (v != null) return v === '1' } catch {} }
    return defaultOpen
  })
  const toggle = () => {
    if (!collapsible) return
    setOpen(o => {
      const n = !o
      if (storageKey) { try { localStorage.setItem(storageKey, n ? '1' : '0') } catch {} }
      return n
    })
  }
  return (
    <div className={cx('cm-section', className)}>
      {title != null && (
        <div className={cx('cm-section-title', collapsible && 'cm-collapsible')} onClick={toggle}>
          {collapsible && <span className="cm-caret">{open ? '▾' : '▸'}</span>}
          {title}
        </div>
      )}
      {open && children}
    </div>
  )
}

// One horizontal row of controls. `label` adds a small caps caption on the
// left; `split` puts label left / control right (stacked settings lists).
export function MenuRow({ label, title, split = false, wrap = false, className, settingId, children }) {
  return (
    <div className={cx('cm-row', split && 'cm-row-split', wrap && 'cm-row-wrap', className)} title={title} data-setting-id={settingId}>
      {label != null && <span className={split ? 'cm-row-name' : 'cm-row-label'}>{label}</span>}
      {split ? <span className="cm-row-ctl">{children}</span> : children}
    </div>
  )
}

export function MenuLabel({ children, title, className }) {
  return <span className={cx('cm-label', className)} title={title}>{children}</span>
}

// Vertical bar between controls in a row; `horizontal` → full-width rule
// between items of an action list.
export function MenuDivider({ horizontal = false }) {
  return <div className={horizontal ? 'cm-hr' : 'cm-divider'} />
}

// ─── Controls ───────────────────────────────────────────────────────────

export function MenuToggle({ on, onChange, disabled, title, className, settingId, children }) {
  return (
    <button type="button" className={cx('cm-toggle', on && 'on', className)}
      disabled={disabled} title={title} data-setting-id={settingId}
      onClick={() => onChange?.(!on)}>
      {children}
    </button>
  )
}

// Button + dropdown list. Owns its open state; closes on pick or on a
// mousedown anywhere outside it. `children` render under the options
// (e.g. the S/R menu's Ray checkbox).
export function MenuSelect({ value, options, onChange, title, renderValue, renderOption, optionClass, className, settingId, children }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  const cur = options.find(o => o.value === value) || null
  return (
    <div className={cx('cm-select', className)} ref={wrapRef} data-setting-id={settingId}>
      <button type="button" className={cx('cm-btn', open && 'active')} title={title} onClick={() => setOpen(o => !o)}>
        {renderValue ? renderValue(cur, value) : cur?.label}
        <span className="caret">▼</span>
      </button>
      {open && (
        <div className="cm-dropdown">
          {options.map(o => {
            const sel = o.value === value
            return (
              <button type="button" key={String(o.value)}
                className={cx(optionClass || 'cm-option', sel && 'selected')}
                title={o.title}
                onClick={() => { onChange?.(o.value); setOpen(false) }}>
                {renderOption ? renderOption(o, sel) : o.label}
              </button>
            )
          })}
          {children}
        </div>
      )}
    </div>
  )
}

// Line-weight picker with stroke previews (S/R, AVWAP, ATR, VA, Absorption).
export function MenuWeight({ value, onChange, weights = [1, 2, 3, 4], title }) {
  return (
    <MenuSelect
      value={value}
      title={title}
      options={weights.map(w => ({ value: w, label: `${w}px` }))}
      onChange={onChange}
      optionClass="cm-weight-option"
      renderValue={() => (<><div className="cm-weight-preview" style={{ height: value }} />{value}px</>)}
      renderOption={(o) => (<><div className="cm-weight-line" style={{ height: o.value }} /><span className="cm-weight-label">{o.label}</span></>)}
    />
  )
}

// Number field with debounced auto-apply. Commits `delay` ms after typing
// stops (0 = every keystroke), and at once on blur / Enter / unmount, only
// when the text parses — clamped up to `min` (and down to `max` with
// clampMax). Blur shows the committed value.
// Never re-seeded from `value` while focused, so a half-typed number holds.
export function MenuNumber({ value, onCommit, min, max, step, float = false, clampMax = false, delay = 200, width, title, className, label, settingId }) {
  const [text, setText] = useState(value ?? '')
  const focusRef = useRef(false)
  const timerRef = useRef(null)
  const pendingRef = useRef(null)
  const commitRef = useRef(onCommit)
  commitRef.current = onCommit
  useEffect(() => { if (!focusRef.current) setText(value ?? '') }, [value])
  const parse = (raw) => {
    const n = float ? parseFloat(raw) : parseInt(raw)
    if (!Number.isFinite(n)) return null
    const lo = Number.isFinite(min) ? Math.max(min, n) : n
    return clampMax && Number.isFinite(max) ? Math.min(max, lo) : lo
  }
  const flush = () => {
    clearTimeout(timerRef.current); timerRef.current = null
    if (pendingRef.current == null) return
    const n = parse(pendingRef.current)
    pendingRef.current = null
    if (n != null) commitRef.current?.(n)
  }
  useEffect(() => () => flush(), [])   // last keystroke survives the menu closing
  const input = (
    <input
      className={cx('cm-num', className)}
      type="number" min={min} max={max} step={step}
      data-setting-id={settingId}
      value={text}
      title={title}
      style={width ? { width } : undefined}
      onFocus={() => { focusRef.current = true }}
      onChange={(e) => {
        const raw = e.target.value
        setText(raw)
        pendingRef.current = raw
        if (delay > 0) { clearTimeout(timerRef.current); timerRef.current = setTimeout(flush, delay) }
        else flush()
      }}
      onBlur={(e) => {
        focusRef.current = false
        const raw = e.target.value
        flush()
        const n = parse(raw)
        setText(n != null ? n : (value ?? ''))
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
      onMouseDown={(e) => e.stopPropagation()}
    />
  )
  if (label == null) return input
  return <><MenuLabel>{label}</MenuLabel>{input}</>
}

// Free text field (S/R label). Applies on every change, like before.
export function MenuText({ value, onChange, placeholder, maxLength, width, className }) {
  return (
    <input className={cx('cm-text', className)} type="text" value={value}
      maxLength={maxLength} placeholder={placeholder}
      style={width ? { width } : undefined}
      onChange={(e) => onChange?.(e.target.value)}
      onMouseDown={(e) => e.stopPropagation()} />
  )
}

// The existing swatch picker (ColorPicker), optionally captioned.
export function MenuSwatch({ label, value, onChange, title, size, settingId }) {
  const cp = <ColorPicker value={value} onChange={onChange} title={title} {...(size ? { size } : null)} />
  const pick = settingId ? <span data-setting-id={settingId} style={{ display: 'inline-flex' }}>{cp}</span> : cp
  if (label == null) return pick
  return <><MenuLabel>{label}</MenuLabel>{pick}</>
}

// Action buttons. variant: 'danger' (DELETE/HIDE), 'primary' (APPLY/CLOSE),
// 'ghost' (RESET/THEME), 'outline' (Curved), 'icon' (🔔), default = toolbar.
export function MenuButton({ variant, on, className, children, ...rest }) {
  const v = variant ? `cm-act cm-act-${variant}` : 'cm-btn'
  return <button type="button" className={cx(v, on && 'on', className)} {...rest}>{children}</button>
}

// Bottom row of action buttons.
export function MenuActions({ children, className }) {
  return <div className={cx('cm-actions', className)}>{children}</div>
}

// ‹ value › over preset stops.
export function MenuStepper({ value, options, format = (v) => v, onChange }) {
  let idx = 0, bd = Infinity
  for (let i = 0; i < options.length; i++) { const d = Math.abs(options[i] - value); if (d < bd) { bd = d; idx = i } }
  const go = (dir) => {
    const ni = Math.max(0, Math.min(options.length - 1, idx + dir))
    if (ni !== idx) onChange?.(options[ni])
  }
  return (
    <div className="cm-stepper">
      <button type="button" className="cm-step" onClick={() => go(-1)} disabled={idx === 0}>‹</button>
      <span className="cm-step-val">{format(value)}</span>
      <button type="button" className="cm-step" onClick={() => go(1)} disabled={idx === options.length - 1}>›</button>
    </div>
  )
}

// zindex-20261102: chart layer for an indicator. Footprint = 0.
// -1 = under the footprint, 0 = default (unchanged), +1 = over the footprint.
const INDEX_STOPS = [-1, 0, 1]
export function MenuIndex({ value, onChange }) {
  const v = Number.isFinite(value) ? value : 0
  return (
    <MenuRow label="Index" title="Layer vs the footprint (0): -1 below, +1 above">
      <MenuStepper value={v} options={INDEX_STOPS} format={(n) => (n > 0 ? `+${n}` : `${n}`)} onChange={onChange} />
    </MenuRow>
  )
}

// ─── Action lists (right-click menu, beaker) ───────────────────────────

// One item. `gear` → the narrow ⚙ button that sits beside an item.
export function MenuItem({ gear = false, className, settingId, children, ...rest }) {
  return (
    <button type="button" className={cx('cm-item', gear && 'cm-item-gear', className)} data-setting-id={settingId} {...rest}>
      {children}
    </button>
  )
}

// Item + its ⚙ side by side.
export function MenuItemRow({ children }) {
  return <div className="cm-item-row">{children}</div>
}
